import {
  DisplayMarketAsset,
  GlobalStateService,
  makeAmount,
  MarketAssetMeta,
  PERCENT_ZERO,
} from "@silo/types";
import { Effect } from "effect";
import { getTokenLabels } from "../utils/get-token-labels";
import { ContractsClient } from "../contracts-client";
import { convertToUsd } from "@silo/utils";
import { getMerklRewardsByType } from "../utils/get-merkl-reward";
import {
  getConfigDepositMode,
  getConfigIsDebtAprFixed,
  getConfigPointsByType,
  GLOBAL_CONFIG,
} from "@silo/config";
import { MerklCampaignMapService } from "../merkl-client/merkl-campaign-map";
import { getDebtTotalApr, getDepositTotalApr } from "./utils";
import { trackIncentivizedShareTokens } from "./track-incentivized-share-tokens";

export type GetFallbackDisplayMarketAsset = ReturnType<
  typeof getFallbackDisplayMarketAsset
>;
export type GetFallbackDisplayMarketAssetSuccess =
  Effect.Effect.Success<GetFallbackDisplayMarketAsset>;
export type GetFallbackDisplayMarketAssetError =
  Effect.Effect.Error<GetFallbackDisplayMarketAsset>;
export type GetFallbackDisplayMarketAssetContext =
  Effect.Effect.Context<GetFallbackDisplayMarketAsset>;

export function getFallbackDisplayMarketAsset(
  marketAssetMeta: MarketAssetMeta
) {
  return Effect.gen(function* () {
    const {
      protocolKey,
      chainKey,
      tokenAddress,
      decimals,
      marketAddress,
      assetKey,
      collateralShareToken,
      protectedShareToken,
      debtShareToken,
    } = marketAssetMeta;

    const { symbol, name } = yield* getTokenLabels(marketAssetMeta);

    const marketAssetState = yield* ContractsClient.getMarketAssetState(
      marketAssetMeta
    );

    const {
      priceUsd,
      liquidity,
      collateralTotal,
      collateralBaseApr,
      collateralRewardsApr,
      collateralEmissionPerSecond,
      protectedTotal,
      protectedRewardsApr,
      protectedEmissionPerSecond,
      debtBaseApr,
      debtRewardsApr,
      debtEmissionPerSecond,
      isPaused,
    } = marketAssetState;

    yield* trackIncentivizedShareTokens({
      collateralShareToken,
      collateralEmissionPerSecond,
      protectedShareToken,
      protectedEmissionPerSecond,
      debtShareToken,
      debtEmissionPerSecond,
    });

    const liquidityUsd = convertToUsd(liquidity, decimals, priceUsd);
    const tvl = makeAmount(collateralTotal + protectedTotal);
    const tvlUsd = convertToUsd(tvl, decimals, priceUsd);

    const merklCampaignMap = yield* MerklCampaignMapService;

    const assetMerklCampaigns = merklCampaignMap.getOrEmpty({
      protocolKey,
      marketAddress,
      tokenAddress,
    });

    const { collateralMerklRewards, protectedMerklRewards, debtMerklRewards } =
      getMerklRewardsByType(assetMerklCampaigns, {
        tokenAddress,
        symbol,
        decimals,
        ...marketAssetState,
      });

    const { collateralPoints, protectedPoints, debtPoints } =
      getConfigPointsByType({
        protocolKey,
        marketAddress,
        assetKey,
      });

    const isDebtAprFixed = getConfigIsDebtAprFixed({
      protocolKey,
      marketAddress,
      assetKey,
    });

    const depositMode = getConfigDepositMode({
      protocolKey,
      marketAddress,
      assetKey,
    });

    const globalState = yield* GlobalStateService;
    const { revenue } = GLOBAL_CONFIG;

    const revenueApr =
      revenue.protocolKey === protocolKey &&
      revenue.marketAddress === marketAddress &&
      revenue.targetTokenAddress === tokenAddress
        ? globalState.revenueApr
        : PERCENT_ZERO;

    const depositTotalApr = getDepositTotalApr({
      collateralBaseApr,
      collateralRewardsApr,
      collateralMerklRewards,
      protectedRewardsApr,
      protectedMerklRewards,
      depositMode,
      revenueApr,
    });

    const debtTotalApr = getDebtTotalApr({
      debtBaseApr,
      debtRewardsApr,
      debtMerklRewards,
    });

    const displayMarketAsset: DisplayMarketAsset = {
      protocolKey,
      chainKey,
      tokenAddress,
      symbol,
      name,
      decimals,
      liquidityUsd,
      tvlUsd,
      collateralBaseApr,
      collateralRewardsApr,
      collateralMerklRewards,
      collateralPoints,
      protectedRewardsApr,
      protectedMerklRewards,
      protectedPoints,
      debtBaseApr,
      debtRewardsApr,
      debtMerklRewards,
      debtPoints,
      isPaused,
      isDebtAprFixed,
      depositMode,
      depositTotalApr,
      debtTotalApr,
    };

    return displayMarketAsset;
  });
}
